import { Palette, alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F6F8FB',
  200: '#E9EEF5',
  300: '#D6DEE8',
  400: '#B3BECC',
  500: '#8A96A6',
  600: '#667489',
  700: '#445063',
  800: '#1C2533',
  900: '#0B1220',
};

const PRIMARY = {
  lighter: '#DCEBFF',
  light: '#8BB6FF',
  main: '#2F7BFF',
  dark: '#1A56D6',
  darker: '#0B318F',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#D7F8F2',
  light: '#7CE7D7',
  main: '#1FB9AD',
  dark: '#12857E',
  darker: '#0B4F4A',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#D6F5FF',
  light: '#8AD9FF',
  main: '#3AA7FF',
  dark: '#1C6CD6',
  darker: '#123E8A',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#DDFBE8',
  light: '#86EEC3',
  main: '#22C55E',
  dark: '#15803D',
  darker: '#0B4A24',
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: '#FFF2CC',
  light: '#FFD166',
  main: '#F59E0B',
  dark: '#B45309',
  darker: '#7C2D12',
  contrastText: '#1B1F24',
};

const ERROR = {
  lighter: '#FFE4E6',
  light: '#FEB2B2',
  main: '#EF4444',
  dark: '#B91C1C',
  darker: '#7F1D1D',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.28),
  action: {
    hover: alpha(GREY[500], 0.12),
    selected: alpha(GREY[500], 0.2),
    disabled: alpha(GREY[500], 0.5),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.12,
    disabledOpacity: 0.38,
  },
};

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[900],
      secondary: GREY[700],
      disabled: GREY[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#EEF3FA',
      neutral: GREY[200],
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
    custom: {
      siteBackground: GREY[200],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: GREY[100],
      secondary: GREY[400],
      disabled: GREY[600],
    },
    background: {
      paper: '#111827',
      default: GREY[900],
      neutral: '#1C2736',
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
    divider: alpha(GREY[400], 0.24),
  } as const;

  return themeMode === 'light' ? light : dark;
}
